import dayjs from "dayjs";
import {
  ALL_TIME_RANGE,
  ReportMonthRange
} from "catchment-common/interfaces/dashboardSurface";

/**
 * The user's chosen reporting window, as a token.
 *
 * Presets resolve to month bounds here on the frontend — the backend only ever
 * receives `{ startDate, endDate }` — so adding or renaming one never needs a
 * deploy. Deliberately absent: "Last 30 days", which the month-keyed data could
 * only ever honour as "this month", and a label that lies gets filed as a bug.
 */
export enum PeriodPreset {
  AllTime = "all-time",
  ThisMonth = "this-month",
  Last3Months = "last-3-months",
  FyToDate = "fy-to-date",
  LastFy = "last-fy",
  Last12Months = "last-12-months"
}

/** Menu order. All time leads because it is the default. */
export const PERIOD_PRESETS: PeriodPreset[] = [
  PeriodPreset.AllTime,
  PeriodPreset.ThisMonth,
  PeriodPreset.Last3Months,
  PeriodPreset.FyToDate,
  PeriodPreset.LastFy,
  PeriodPreset.Last12Months
];

/** The Australian financial year starts on 1 July (month index 6). */
const FY_START_MONTH = 6;

const fyStartYear = (now: dayjs.Dayjs): number =>
  now.month() >= FY_START_MONTH ? now.year() : now.year() - 1;

/**
 * Resolve a preset to the inclusive month bounds it covers.
 *
 * Every range covers whole reporting months: reporting is month-grained, and a
 * report month is stored as its last day, so a bound mid-month would silently
 * include or drop a whole month's figures. All time resolves to the sentinels
 * rather than to `undefined`, keeping every request shape identical.
 *
 * `now` is injectable so the presets can be tested at a fixed date.
 */
export const resolvePeriod = (
  preset: PeriodPreset,
  now: Date = new Date()
): ReportMonthRange => {
  const today = dayjs(now);
  const endDate = today.endOf("month").format("YYYY-MM-DD");
  const from = (start: dayjs.Dayjs): ReportMonthRange => ({
    endDate,
    startDate: start.startOf("month").format("YYYY-MM-DD")
  });

  switch (preset) {
    case PeriodPreset.ThisMonth:
      return from(today);
    case PeriodPreset.Last3Months:
      // Trailing, including the current month — so two months back, not three.
      return from(today.subtract(2, "month"));
    case PeriodPreset.FyToDate:
      return from(dayjs(new Date(fyStartYear(today), FY_START_MONTH, 1)));
    case PeriodPreset.LastFy: {
      // The whole previous financial year: 1 July a year before the current
      // FY started, through the 30 June the current FY started after.
      const currentFyStart = fyStartYear(today);
      return {
        endDate: dayjs(new Date(currentFyStart, FY_START_MONTH, 1))
          .subtract(1, "day")
          .format("YYYY-MM-DD"),
        startDate: dayjs(
          new Date(currentFyStart - 1, FY_START_MONTH, 1)
        ).format("YYYY-MM-DD")
      };
    }
    case PeriodPreset.Last12Months:
      return from(today.subtract(11, "month"));
    case PeriodPreset.AllTime:
    default:
      return ALL_TIME_RANGE;
  }
};

/** Menu label for a preset, spelled as the analytics glossary spells it. The
 * financial year names itself ("FY 2026-27 to date") because "this FY" is
 * ambiguous in July. */
export const periodLabel = (
  preset: PeriodPreset,
  now: Date = new Date()
): string => {
  switch (preset) {
    case PeriodPreset.ThisMonth:
      return "This Month";
    case PeriodPreset.Last3Months:
      return "Last 3 Months";
    case PeriodPreset.FyToDate: {
      const start = fyStartYear(dayjs(now));
      return `FY ${start}-${String(start + 1).slice(-2)} To Date`;
    }
    case PeriodPreset.LastFy: {
      const start = fyStartYear(dayjs(now)) - 1;
      return `FY ${start}-${String(start + 1).slice(-2)}`;
    }
    case PeriodPreset.Last12Months:
      return "Last 12 Months";
    case PeriodPreset.AllTime:
    default:
      return "All Time";
  }
};
